import { findSellingDataChecklistOfEstates } from '@/api/selling.api';
import { useRouteParams } from '@/hooks';
import type { ISellingDataChecklistQuery, SellingChecklistDataPoints, SellingStates } from '@condo/domain';
import { useQuery } from '@tanstack/vue-query';
import { type MaybeRef, computed } from 'vue';
import { ref, unref } from 'vue';

export const useSellingDataChecklistFilter = (initialOverride?: Record<any, any>) => {
    const { getRouteParam, getRouteParamArray } = useRouteParams();

    const defaultFilter: ISellingDataChecklistQuery = {
        page: 1,
        size: 10,
        order: 'DESC',
        orderBy: 'estateId',
        condoUuid: null,
        sellingState: null,
        region: null,
        missingDataPoints: [],
        showCompleted: false,
        showOnlyVisibleInSalesPortal: false,
        salesPackageId: [],
    };

    const filter = ref<ISellingDataChecklistQuery>({
        page: getRouteParam('page', Number) ?? defaultFilter.page,
        size: getRouteParam('size', Number) ?? defaultFilter.size,
        order: (getRouteParam('order', String) as 'ASC' | 'DESC') ?? defaultFilter.order,
        orderBy: getRouteParam('orderBy', String) ?? defaultFilter.orderBy,
        region: getRouteParamArray('region', String, defaultFilter.region) as string[],
        condoUuid: getRouteParam('condoUuid', String) ?? defaultFilter.condoUuid,
        sellingState: getRouteParamArray('sellingState', String, defaultFilter.sellingState) as SellingStates[],
        missingDataPoints: getRouteParamArray('missingDataPoints', String, defaultFilter.missingDataPoints) as SellingChecklistDataPoints[],
        showCompleted: getRouteParam('showCompleted', String) === 'true' || defaultFilter.showCompleted,
        showOnlyVisibleInSalesPortal: getRouteParam('showOnlyVisibleInSalesPortal', String) === 'true' || defaultFilter.showOnlyVisibleInSalesPortal,
        salesPackageId: getRouteParamArray('salesPackageId', Number, defaultFilter.salesPackageId),
        ...(initialOverride ?? {}),
    });

    const setFilter = async updatedFilter => {
        filter.value = {
            ...defaultFilter,
            ...updatedFilter,
        };
    };

    const resetFilter = async () => {
        filter.value = {
            ...defaultFilter,
            missingDataPoints: defaultFilter.missingDataPoints,
        };
    };

    return { filter, setFilter, resetFilter };
};

export const useSellingDataChecklist = (params?: MaybeRef<ISellingDataChecklistQuery>, enabled = true) => {
    const queryParams = computed(() => unref(params));

    return useQuery(['selling-data-checklist', queryParams], async () => findSellingDataChecklistOfEstates(queryParams.value), {
        initialData: {
            estates: [],
            total: 0,
        },
        enabled,
    });
};
